<template>
    <Portal to="sidepanel">
        <SidePanel
            key="activeLicenseSidePanel"
            name="Activate License Code"
            class="activate-license"
            width="medium"
            :is-dark-mode="isDarkMode"
            @close="emitClose"
        >
            <PocketInput
                v-model="firstName"
                :is-dark-mode="isDarkMode"
                label="First Name"
                :error="errorFields.includes('firstName')"
                class="activate-license__input"
                @keydown.enter="activateLicense"
            />
            <PocketInput
                v-model="lastName"
                :is-dark-mode="isDarkMode"
                label="Last Name"
                :error="errorFields.includes('lastName')"
                class="activate-license__input"
                @keydown.enter="activateLicense"
            />
            <LicenseField v-model="licenseKey" />
            <div class="activate-license__license-info">
                License codes are purchased by your school or workplace. If you haven't received a license code,
                contact your organization.
            </div>

            <template #action>
                <PocketButton
                    :is-dark-mode="isDarkMode"
                    :disabled="!licenseKey || !firstName || !lastName"
                    :is-loading="isLoading"
                    @click="activateLicense"
                >
                    Activate
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { licenseModule } from '@/store/license/module'
import UIKit from '@pocketprep/ui-kit'
import LicenseField from '@/components/Settings/ActivateLicense/LicenseField.vue'
import { toastModule } from '@/store/toast/module'
import { userModule } from '@/store/user/module'

const {
    SidePanel,
    Input: PocketInput,
    Button: PocketButton,
} = UIKit

const emit = defineEmits<{
    close: []
}>()

const route = useRoute()

const licenseKey = ref('')
const errorFields = ref<string[]>([])
const firstName = ref('')
const lastName = ref('')
const isLoading = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const studentLmsIdQueryParam = computed(() => {
    return typeof route.query?.studentLmsId === 'string' ? route.query.studentLmsId : ''
})

onMounted(async () => {
    await userModule.actions.fetchUserData()

    firstName.value = (typeof route.query.firstName === 'string' && route.query.firstName)
        ? route.query.firstName
        : userModule.state.user?.firstName || ''
    lastName.value = (typeof route.query.lastName === 'string' && route.query.lastName)
        ? route.query.lastName
        : userModule.state.user?.lastName || ''
})

const activateLicense = async () => {
    errorFields.value = []
    isLoading.value = true

    if (!firstName.value) {
        errorFields.value.push('firstName')
    }
    if (!lastName.value) {
        errorFields.value.push('lastName')
    }

    if (!errorFields.value.length) {
        try {
            // Validate license to get the exam
            const license = await licenseModule.actions.validateLicense(licenseKey.value)
            await Promise.all([
                userModule.actions.updateUser({
                    firstName: firstName.value,
                    lastName: lastName.value,
                }),
                licenseModule.actions.activateLicense(license, {
                    studentLmsId: studentLmsIdQueryParam.value,
                }),
            ])
            toastModule.actions.displayToast({
                title: 'License code activated.',
                subtext: `You are now studying the ${license.examName} exam. Switch
                    exams via the upper left menu dropdown`,
            })
        } catch (err) {
            // noop
        }
        emitClose()
    }
    isLoading.value = false
}

const emitClose = () => {
    emit('close')
}
</script>

<style lang="scss" scoped>
.activate-license {
    &__input {
        margin: 0 -12px 23px;
    }

    &__license-info {
        font-size: 14px;
        line-height: 18px;
        margin-top: -14px;
        color: $ash;
        max-width: 372px;
    }
}
</style>
